import { useEffect, useState } from 'react';
import {
  ExSideDrawer,
  ExButton,
  ButtonType,
  ButtonSize,
  ButtonFlavor,
  ExLoader,
  LoaderSize,
  LoaderVariant,
  ExEmptyState,
} from '@boomi/exosphere';
import { useTranslation } from 'react-i18next';
import modelsApiService from '@services/agent-foundation/agent-foundation-models-api-service';
import providerLogo from '../providerLogo';
import ModelConfigCard from '../ModelConfigCard/ModelConfigCard';
import { ModelConfig } from '../types/models.types';
import './RegisteredModelConfigurationsList.scss';

interface RegisteredModelConfigurationsListProps {
  open: boolean;
  onClose: () => void;
  onCancel?: () => void;
  provider: string;
  modelId: string;
  modelConfigId: string;
  modelName: string;
  modelDisplayName?: string;
  modelDescription?: string;
  onSelectConfiguration?: (config: ModelConfig) => void;
}

function RegisteredModelConfigurationsList({
  open,
  onClose,
  onCancel: handleCancel = onClose,
  provider,
  modelId,
  modelConfigId,
  modelName,
  modelDisplayName,
  modelDescription,
  onSelectConfiguration,
}: RegisteredModelConfigurationsListProps) {
  const [t] = useTranslation('common');
  const [configurations, setConfigurations] = useState<ModelConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) return;

    const fetchConfigurations = async () => {
      setIsLoading(true);
      try {
        const configs = await modelsApiService.getConfigurationsByProviderAndModel(
          provider,
          modelId,
        );
        setConfigurations(configs);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load configurations:', error);
        setConfigurations([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigurations();
  }, [open, provider, modelId]);

  const handleSave = () => {
    if (selectedConfigId) {
      const selectedConfig = configurations.find((c) => c.id === selectedConfigId);
      if (selectedConfig && onSelectConfiguration) {
        onSelectConfiguration(selectedConfig);
        onClose();
      }
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="registered-model-configurations-list__loader">
          <ExLoader
            size={LoaderSize.MEDIUM}
            label={t('ai_loading')}
            variant={LoaderVariant.SPINNER}
            data-testid="registered-model-configurations-list-loader"
          />
        </div>
      );
    }

    return (
      <div className="registered-model-configurations-list__body">
        {/* Model Details Section */}
        <div className="registered-model-configurations-list__model-details">
          <span className="registered-model-configurations-list__model-name-label">
            {t('model')}
          </span>
          <div className="registered-model-configurations-list__model-info">
            <span className="registered-model-configurations-list__model-icon">
              <img
                src={providerLogo(provider).src}
                alt={providerLogo(provider).alt}
              />
            </span>
            <span className="registered-model-configurations-list__model-name">
              {modelDisplayName || modelName}
            </span>
          </div>
          {modelDescription && (
            <div className="registered-model-configurations-list__model-description">
              <span className="registered-model-configurations-list__description-label">
                {t('description')}
              </span>
              <span className="registered-model-configurations-list__description-text">
                {modelDescription}
              </span>
            </div>
          )}
        </div>

        {/* Select Configuration Section */}
        <div className="registered-model-configurations-list__select-section">
          <h4 className="registered-model-configurations-list__select-title">
            {t('models_registered_configs_select_title')}
          </h4>

          {configurations.length > 0 ? (
            <div className="registered-model-configurations-list__configurations">
              {configurations.map((config) => (
                <ModelConfigCard
                  key={config.id}
                  config={config}
                  selected={(selectedConfigId ?? modelConfigId) === config.id}
                  onSelect={() => setSelectedConfigId(config.id)}
                />
              ))}
            </div>
          ) : (
            <ExEmptyState
              label={t('models_registered_configs_empty_label')}
              text={t('models_registered_configs_empty_text')}
              data-testid="registered-model-configurations-empty-state"
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <ExSideDrawer
      open={open}
      navigation
      onClose={onClose}
      onCancel={handleCancel}
      panelTitle={t('models_my_models_tab')}
      className="registered-model-configurations-list"
    >
      {renderContent()}

      <div slot="footer" className="registered-model-configurations-list__footer">
        <ExButton
          type={ButtonType.SECONDARY}
          size={ButtonSize.DEFAULT}
          flavor={ButtonFlavor.BRANDED}
          onClick={onClose}
        >
          {t('models_button_close')}
        </ExButton>
        <ExButton
          type={ButtonType.PRIMARY}
          size={ButtonSize.DEFAULT}
          flavor={ButtonFlavor.BASE}
          onClick={handleSave}
          disabled={!selectedConfigId}
        >
          {t('models_button_save')}
        </ExButton>
      </div>
    </ExSideDrawer>
  );
}
RegisteredModelConfigurationsList.defaultProps = {
  modelDisplayName: '',
  modelDescription: '',
  onCancel: undefined,
  onSelectConfiguration: undefined,
};
export default RegisteredModelConfigurationsList;
